import { useEffect, useState } from "react";
import api from "../../axiosconfig";

const DEFAULT_AVERAGES = {
  physical: 0,
  emotional: 0,
  acceptance: 0,
  relationships: 0,
  loving_kindness: 0,
  intellectual: 0,
};

/**
 * Hook to fetch and compare assessment averages for two consecutive months.
 * By default compares M-2 vs M-1 (the two months before the current month).
 * Can optionally accept custom month dates for explicit month-to-month comparison.
 * 
 * @param {Date} customStartDate - Optional custom start date for first range
 * @param {Date} customEndDate - Optional custom end date for second range
 * 
 * Returns data structure:
 * {
 *   previousMonth: { month: "December", year: 2025, averages: {...} },
 *   monthBefore: { month: "November", year: 2025, averages: {...} },
 *   loading: boolean,
 *   error: string | null,
 *   hasData: boolean
 * }
 */
const startOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1);
const endOfMonth = (d) => new Date(d.getFullYear(), d.getMonth() + 1, 0);

// Format date as YYYY-MM-DD in local time to avoid timezone shifting to the previous day
const formatDateLocal = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

export default function useUserRangeAverages(customStartDate = null, customEndDate = null) {
  const [comparisonData, setComparisonData] = useState({
    previousMonth: null,
    monthBefore: null,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchRangeAverages = async () => {
      try {
        setLoading(true);
        setError(null);

        // Use custom dates if provided, otherwise default to M-2 vs M-1
        if (customStartDate && customEndDate) {
          const selectedStartMonth = startOfMonth(new Date(customStartDate));
          const selectedEndMonth = startOfMonth(new Date(customEndDate));

          const monthBeforeStart =
            selectedStartMonth <= selectedEndMonth
              ? selectedStartMonth
              : selectedEndMonth;
          const rangeStart =
            selectedStartMonth <= selectedEndMonth
              ? selectedEndMonth
              : selectedStartMonth;
          const monthBeforeEnd = endOfMonth(monthBeforeStart);
          const rangeEnd = endOfMonth(rangeStart);

          // Get monthBefore averages (earlier range)
          const monthBeforeResponse = await api.get(
            "/api/assessments/user-range-average",
            {
              params: {
                range_start: formatDateLocal(monthBeforeStart),
                range_end: formatDateLocal(monthBeforeEnd),
              },
            }
          );

          // Get current range averages (later range)
          const currentRangeResponse = await api.get(
            "/api/assessments/user-range-average",
            {
              params: {
                range_start: formatDateLocal(rangeStart),
                range_end: formatDateLocal(rangeEnd),
              },
            }
          );

          const monthBeforePayload = monthBeforeResponse?.data?.averages || {};
          const currentRangePayload = currentRangeResponse?.data?.averages || {};

          const getMonthName = (date) =>
            date.toLocaleString("en-US", { month: "long" });

          setComparisonData({
            monthBefore: {
              month: getMonthName(monthBeforeStart),
              year: monthBeforeStart.getFullYear(),
              averages: {
                ...DEFAULT_AVERAGES,
                ...monthBeforePayload,
              },
              overallScore: monthBeforeResponse?.data?.overallScore || null,
              totalAssessments:
                monthBeforeResponse?.data?.metrics?.total_assessments || 0,
            },
            previousMonth: {
              month: getMonthName(rangeStart),
              year: rangeStart.getFullYear(),
              averages: {
                ...DEFAULT_AVERAGES,
                ...currentRangePayload,
              },
              overallScore: currentRangeResponse?.data?.overallScore || null,
              totalAssessments:
                currentRangeResponse?.data?.metrics?.total_assessments || 0,
            },
          });
        } else {
          // Default: M-2 vs M-1
          const now = new Date();
          const currentMonth = now.getMonth();
          const currentYear = now.getFullYear();

          // M-1: Previous month (full month)
          const prevMonth = currentMonth === 0 ? 11 : currentMonth - 1;
          const prevYear = currentMonth === 0 ? currentYear - 1 : currentYear;
          const prevMonthStart = new Date(prevYear, prevMonth, 1);
          const prevMonthEnd = new Date(prevYear, prevMonth + 1, 0);

          // M-2: Month before previous
          const monthBefore = prevMonth === 0 ? 11 : prevMonth - 1;
          const monthBeforeYear = prevMonth === 0 ? prevYear - 1 : prevYear;
          const monthBeforeStart = new Date(monthBeforeYear, monthBefore, 1);
          const monthBeforeEnd = new Date(monthBeforeYear, monthBefore + 1, 0);

          // Get monthBefore (M-2) averages
          const monthBeforeResponse = await api.get(
            "/api/assessments/user-range-average",
            {
              params: {
                range_start: formatDateLocal(monthBeforeStart),
                range_end: formatDateLocal(monthBeforeEnd),
              },
            }
          );

          // Get previous month (M-1) averages
          const prevMonthResponse = await api.get(
            "/api/assessments/user-range-average",
            {
              params: {
                range_start: formatDateLocal(prevMonthStart),
                range_end: formatDateLocal(prevMonthEnd),
              },
            }
          );

          // Format month names
          const getMonthName = (date) =>
            date.toLocaleString("en-US", { month: "long" });

          const monthBeforePayload = monthBeforeResponse?.data?.averages || {};
          const prevMonthPayload = prevMonthResponse?.data?.averages || {};

          setComparisonData({
            monthBefore: {
              month: getMonthName(monthBeforeStart),
              year: monthBeforeYear,
              averages: {
                ...DEFAULT_AVERAGES,
                ...monthBeforePayload,
              },
              overallScore: monthBeforeResponse?.data?.overallScore || null,
              totalAssessments:
                monthBeforeResponse?.data?.metrics?.total_assessments || 0,
            },
            previousMonth: {
              month: getMonthName(prevMonthStart),
              year: prevYear,
              averages: {
                ...DEFAULT_AVERAGES,
                ...prevMonthPayload,
              },
              overallScore: prevMonthResponse?.data?.overallScore || null,
              totalAssessments:
                prevMonthResponse?.data?.metrics?.total_assessments || 0,
            },
          });
        }
      } catch (e) {
        console.error("[useUserRangeAverages] fetch error", e);
        setError("Unable to load comparison data.");
        setComparisonData({
          monthBefore: null,
          previousMonth: null,
        });
      } finally {
        setLoading(false);
      }
    };

    fetchRangeAverages();
  }, [customStartDate, customEndDate]);

  const hasData =
    comparisonData.monthBefore &&
    comparisonData.previousMonth &&
    (Object.values(comparisonData.monthBefore.averages).some((v) => Number(v) > 0) ||
      Object.values(comparisonData.previousMonth.averages).some((v) => Number(v) > 0));

  return { ...comparisonData, loading, error, hasData };
}
